/**
 * Dashboard principal del profesor
 */

import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { alumnosProfesorService } from "../../../services/profesor";
import { cursosProfesorService } from "../../../services/profesor/cursos.service";
import { simulacionesProfesorService } from "../../../services/profesor/simulaciones.service";
import { AlumnoProfesor } from "../alumnos/dto/alumnos.dto";
import { ProfesorLayout, ProgresoCard } from "../components";
import { CursoProfesor } from "../cursos/dto/cursos.dto";
import { SimulacionProfesor } from "../simulaciones/dto/simulaciones.dto";

export const ProfesorDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [alumnos, setAlumnos] = useState<AlumnoProfesor[]>([]);
  const [cursos, setCursos] = useState<CursoProfesor[]>([]);
  const [simulaciones, setSimulaciones] = useState<SimulacionProfesor[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);

      console.log("Cargando datos del dashboard...", { user });

      // Cargar datos en paralelo
      const [alumnosData, cursosData, simulacionesData] = await Promise.all([
        alumnosProfesorService.getAlumnos().catch((err) => {
          console.error("Error cargando alumnos:", err);
          return [];
        }),
        cursosProfesorService.getCursos().catch((err) => {
          console.error("Error cargando cursos:", err);
          return [];
        }),
        simulacionesProfesorService.getSimulaciones().catch((err) => {
          console.error("Error cargando simulaciones:", err);
          return [];
        }),
      ]);

      console.log("Datos cargados:", {
        alumnos: alumnosData?.length || 0,
        cursos: cursosData?.length || 0,
        simulaciones: simulacionesData?.length || 0,
        alumnosData,
        cursosData,
        simulacionesData,
      });

      setAlumnos(Array.isArray(alumnosData) ? alumnosData : []);
      setCursos(Array.isArray(cursosData) ? cursosData : []);
      setSimulaciones(Array.isArray(simulacionesData) ? simulacionesData : []);
    } catch (error) {
      console.error("Error general cargando datos:", error);
      setAlumnos([]);
      setCursos([]);
      setSimulaciones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const totalAlumnos = alumnos.length;
  const alumnosVigentes = alumnos.filter((a) => a.vigente).length;
  const totalCursos = cursos.length;
  const cursosVigentes = cursos.filter((c) => c.vigente).length;
  const totalSimulaciones = simulaciones.length;
  const simulacionesFinalizadas = simulaciones.filter(
    (s) => s.estado === "FINALIZADA"
  ).length;

  return (
    <ProfesorLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header mejorado */}
        <div className="mb-10">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-lg p-8 text-white">
            <h1 className="text-4xl font-bold mb-2">Panel de Profesor</h1>
            <p className="text-purple-100 text-lg">
              Bienvenido,{" "}
              <span className="font-semibold">
                {user?.nombres} {user?.apellidos}
              </span>
            </p>
          </div>
        </div>

        {/* Cards de resumen mejoradas con datos reales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <ProgresoCard
            titulo="Total Alumnos"
            valor={totalAlumnos}
            subtitulo={`${alumnosVigentes} vigentes`}
            color="blue"
            icono={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            }
          />
          <ProgresoCard
            titulo="Total Cursos"
            valor={totalCursos}
            subtitulo={`${cursosVigentes} cursos asignados`}
            color="green"
            icono={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            }
          />
          <ProgresoCard
            titulo="Total Simulaciones"
            valor={totalSimulaciones}
            subtitulo={`${simulacionesFinalizadas} finalizadas`}
            color="purple"
            icono={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
          <ProgresoCard
            titulo="Alumnos Activos"
            valor={alumnosVigentes}
            subtitulo={`De ${totalAlumnos} total`}
            color="yellow"
            icono={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />
        </div>

        {/* Secciones de datos organizadas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Sección de Alumnos */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
              <a
                href="/profesor/alumnos"
                className="text-xl font-bold text-gray-900 flex items-center gap-2 hover:text-blue-700 transition-colors cursor-pointer group"
              >
                <svg
                  className="w-5 h-5 text-blue-600 group-hover:text-blue-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                Mis Alumnos
                <svg
                  className="w-4 h-4 text-blue-600 group-hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="ml-auto text-sm font-normal text-gray-600">
                  {totalAlumnos} total
                </span>
              </a>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600"></div>
                </div>
              ) : alumnos.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No hay alumnos asignados
                </div>
              ) : (
                <div className="space-y-2">
                  {alumnos.slice(0, 5).map((alumno) => (
                    <div
                      key={alumno.idUsuario}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-xs">
                          {alumno.nombres.charAt(0)}
                          {alumno.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {alumno.nombres} {alumno.apellidos}
                          </p>
                          <p className="text-xs text-gray-500">
                            {alumno.cursos?.length || 0} curso
                            {alumno.cursos?.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          alumno.vigente
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {alumno.vigente ? "Vigente" : "No vigente"}
                      </span>
                    </div>
                  ))}
                  {alumnos.length > 5 && (
                    <div className="text-center pt-2">
                      <a
                        href="/profesor/alumnos"
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Ver todos ({alumnos.length})
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sección de Cursos */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
              <a
                href="/profesor/cursos"
                className="text-xl font-bold text-gray-900 flex items-center gap-2 hover:text-green-700 transition-colors cursor-pointer group"
              >
                <svg
                  className="w-5 h-5 text-green-600 group-hover:text-green-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                Mis Cursos
                <svg
                  className="w-4 h-4 text-green-600 group-hover:text-green-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <span className="ml-auto text-sm font-normal text-gray-600">
                  {totalCursos} total
                </span>
              </a>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-200 border-t-green-600"></div>
                </div>
              ) : cursos.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No hay cursos asignados
                </div>
              ) : (
                <div className="space-y-2">
                  {cursos.slice(0, 5).map((curso) => (
                    <div
                      key={curso.idCurso}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-semibold text-xs">
                          {curso.nombre.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {curso.nombreCompleto}
                          </p>
                          <p className="text-xs text-gray-500">
                            {curso.colegio.nombre} • {curso.cantidadAlumnos}{" "}
                            alumnos
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          curso.vigente
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {curso.vigente ? "Vigente" : "No vigente"}
                      </span>
                    </div>
                  ))}
                  {cursos.length > 5 && (
                    <div className="text-center pt-2">
                      <a
                        href="/profesor/cursos"
                        className="text-sm text-green-600 hover:text-green-800 font-medium"
                      >
                        Ver todos ({cursos.length})
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sección de Simulaciones */}
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200">
            <a
              href="/profesor/simulaciones"
              className="text-xl font-bold text-gray-900 flex items-center gap-2 hover:text-purple-700 transition-colors cursor-pointer group"
            >
              <svg
                className="w-5 h-5 text-purple-600 group-hover:text-purple-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Simulaciones Recientes
              <svg
                className="w-4 h-4 text-purple-600 group-hover:text-purple-700 opacity-0 group-hover:opacity-100 transition-opacity"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span className="ml-auto text-sm font-normal text-gray-600">
                {totalSimulaciones} total • {simulacionesFinalizadas}{" "}
                finalizadas
              </span>
            </a>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-200 border-t-purple-600"></div>
              </div>
            ) : simulaciones.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No hay simulaciones registradas
              </div>
            ) : (
              <div className="space-y-2">
                {simulaciones
                  .sort(
                    (a, b) =>
                      new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
                  )
                  .slice(0, 5)
                  .map((sim) => (
                    <div
                      key={sim.idSimulacionPAES}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-xs">
                          {sim.alumno.nombres.charAt(0)}
                          {sim.alumno.apellidos.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {sim.alumno.nombreCompleto}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(sim.fecha).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}{" "}
                            • {sim.totalPreguntas} preguntas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            sim.estado === "FINALIZADA"
                              ? "bg-green-100 text-green-800"
                              : sim.estado === "EN_CURSO"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {sim.estado}
                        </span>
                        {sim.estado === "FINALIZADA" && (
                          <span className="text-sm font-semibold text-purple-600">
                            {sim.porcentajeCorrectas.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                {simulaciones.length > 5 && (
                  <div className="text-center pt-2">
                    <a
                      href="/profesor/simulaciones"
                      className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                    >
                      Ver todas ({simulaciones.length})
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ProfesorLayout>
  );
};
